import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Building2, User } from "lucide-react";
import { useCustomers } from "@/lib/queries";
import { addressLine } from "@/lib/format";
import { useScrollRestore } from "@/lib/hooks";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader, LoadingBlock, ErrorBlock, EmptyState } from "@/components/common/Blocks";
import { CompanyMark, customerKind } from "@/components/common/CompanyMark";

const SEGMENTS = [["alle", "Alle"], ["bedrift", "Bedrift"], ["privat", "Privat"]];

const norm = (s = "") => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function KunderPage() {
  const { data, isLoading, isError, refetch } = useCustomers();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const seg = params.get("type") || "alle";
  const setParam = (k, v, dflt) => setParams((p) => { const n = new URLSearchParams(p); if (!v || v === dflt) n.delete(k); else n.set(k, v); return n; }, { replace: true });
  useScrollRestore("scroll:/kunder", !!data);

  const counts = useMemo(() => {
    const all = data || [];
    return { alle: all.length, bedrift: all.filter((c) => c.type !== "privat").length, privat: all.filter((c) => c.type === "privat").length };
  }, [data]);

  const rows = useMemo(() => {
    let list = data || [];
    if (seg === "bedrift") list = list.filter((c) => c.type !== "privat");
    if (seg === "privat") list = list.filter((c) => c.type === "privat");
    if (q) {
      const n = norm(q);
      list = list.filter((c) => [c.name, c.industry, c.org_no, c.address?.city, ...(c.contacts || []).map((p) => p.name)].filter(Boolean).some((v) => norm(String(v)).includes(n)));
    }
    return list;
  }, [data, seg, q]);

  const open = (c) => navigate(`/kunder/${c.id}`);
  const hasAny = q || seg !== "alle";

  return (
    <div className="page" data-testid="customers-page">
      <PageHeader title="Kunder" meta={data ? `${counts.bedrift} bedriftskunder · ${counts.privat} privatkunder · bestiller, fakturamottaker og kontakt på byggeplass kan være ulike personer` : null} />

      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="customers-toolbar">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setParam("q", e.target.value, "")} placeholder="Søk på navn, kontakt, sted eller org.nr." className="h-9 border-0 bg-[hsl(var(--surface-sunken))] pl-9 shadow-none focus-visible:ring-1" data-testid="customers-search-input" aria-label="Søk i kunder" />
        </div>
        <div className="seg" role="group" aria-label="Kundetype" data-testid="customer-type-filter">
          {SEGMENTS.map(([k, l]) => (
            <button key={k} type="button" aria-pressed={seg === k} onClick={() => setParam("type", k, "alle")} data-testid={`customer-type-${k}`}>
              {k === "bedrift" && <Building2 className="h-3.5 w-3.5" aria-hidden="true" />}
              {k === "privat" && <User className="h-3.5 w-3.5" aria-hidden="true" />}
              {l} <span className="seg-count">{counts[k]}</span>
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <LoadingBlock /> : isError ? <ErrorBlock onRetry={refetch} /> : rows.length === 0 ? (
        <EmptyState title="Ingen kunder matcher" description="Prøv et annet søk eller fjern filteret." action={hasAny ? <Button variant="outline" size="sm" onClick={() => setParams(new URLSearchParams(), { replace: true })} data-testid="customers-clear">Nullstill</Button> : null} />
      ) : (
        <div className="frame overflow-x-auto"><table className="data-table" data-testid="customers-table">
          <thead>
            <tr>
              <th className="w-[32%] min-w-[220px]">Kunde</th>
              <th className="hidden md:table-cell">Kontakt</th>
              <th className="hidden lg:table-cell">Sted</th>
              <th className="hidden w-[150px] sm:table-cell">Leveranser</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const company = c.type !== "privat";
              const primary = c.contacts?.[0];
              return (
                <tr key={c.id} className="is-link" tabIndex={0} role="link" onClick={() => open(c)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(c); } }} data-testid={`customer-row-${c.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <CompanyMark name={c.name} type={c.type} logo={c.logo_url} size="md" slot testId={`customer-mark-${c.id}`} />
                      <div className="min-w-0">
                        <p className="row-name sm:truncate">{c.name}</p>
                        <p className="t-meta mt-0.5 sm:truncate">{customerKind(c)}{company && c.org_no ? ` · org.nr. ${c.org_no}` : ""}</p>
                        <p className="t-meta mt-0.5 md:hidden">{primary?.name}{primary?.phone ? ` · ${primary.phone}` : ""}</p>
                        <p className="t-meta mt-0.5 tabular sm:hidden">{c.active_deliveries} aktive · {c.delivered_deliveries} levert</p>
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell">
                    <p className="text-[15px] leading-snug">{primary?.name || "–"}{company && primary?.role ? <span className="t-meta"> · {primary.role.split(" / ")[0]}</span> : null}</p>
                    <p className="t-meta mt-0.5">{[primary?.phone, primary?.email].filter(Boolean).join(" · ")}</p>
                  </td>
                  <td className="hidden text-[14.5px] text-muted-foreground lg:table-cell">{company ? `${c.address?.city || ""}${c.website ? ` · ${c.website}` : ""}` : addressLine(c.address)}</td>
                  <td className="hidden sm:table-cell">
                    <p className="text-[15px] tabular leading-snug">{c.active_deliveries} aktive</p>
                    <p className="t-meta mt-0.5 tabular">{c.delivered_deliveries} levert</p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
